import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { RiskProfilePanel } from '@/components/participants/risk/RiskProfilePanel';
import { StaffShiftBriefView } from '@/components/participants/care-plan/structured/StaffShiftBriefView';
import { CarePlanAcknowledgementDialog } from '@/components/participants/care-plan/CarePlanAcknowledgementDialog';
import { ProgressNoteForm, emptyNoteForm } from '@/components/participants/notes/ProgressNoteForm';
import { useAuth } from '@/lib/auth';
import { notify } from '@/lib/notifications/engine';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { MedicationAllergyCheckModal } from '@/components/clinical/MedicationAllergyCheckModal';
import { WorkerShiftParticipantsPanel } from '@/components/shared/WorkerShiftParticipantsPanel';
import { ShiftAddress, ShiftNotes } from '@/components/shared/ShiftAddressNotes';
import { 
  MapPin, Clock, CheckCircle, LogOut, FileText, ArrowLeft, AlertCircle, 
  Loader2, Send, User, Calendar, Timer, AlertTriangle, XCircle, RotateCcw, ListChecks, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { clockInWindow, clockInClosedText } from '@/lib/clockInWindow';
import {
  formatLateBadge,
  formatEarlyLeaveBadge,
  formatOvertimeBadge,
  formatVarianceTitle,
  isLargeVariance,
} from '@/utils/varianceDisplay';

// ═══════════════════════════════════════════════════════════════════════════════
// ATTENDANCE & APPROVAL STATUS BADGE COMPONENTS (Day 10)
// ═══════════════════════════════════════════════════════════════════════════════

const ATTENDANCE_BADGE_CONFIG = {
  on_time: { label: 'On Time', className: 'bg-green-500 text-white', icon: CheckCircle },
  late_sign_in: { label: 'Late Sign In', className: 'bg-yellow-500 text-black', icon: Clock },
  early_sign_out: { label: 'Early Sign Out', className: 'bg-yellow-500 text-black', icon: LogOut },
  late_and_early: { label: 'Late & Early', className: 'bg-orange-500 text-white', icon: AlertTriangle },
  missing_clock_in: { label: 'Missing Clock In', className: 'bg-red-500 text-white', icon: AlertCircle },
  missing_clock_out: { label: 'Missing Clock Out', className: 'bg-red-500 text-white', icon: AlertCircle },
  missing_both_clocks: { label: 'No Attendance', className: 'bg-red-700 text-white', icon: XCircle },
  overtime: { label: 'Overtime', className: 'bg-blue-500 text-white', icon: Timer },
  attendance_exception: { label: 'Exception', className: 'bg-red-500 text-white', icon: AlertTriangle },
};

const APPROVAL_BADGE_CONFIG = {
  pending_approval: { label: 'Pending Review', className: 'bg-yellow-500 text-black', icon: Clock },
  approved_scheduled: { label: 'Approved', className: 'bg-green-600 text-white', icon: CheckCircle },
  approved_adjusted: { label: 'Approved (Adjusted)', className: 'bg-green-500 text-white', icon: CheckCircle },
  rejected: { label: 'Rejected', className: 'bg-red-600 text-white', icon: XCircle },
  returned_for_correction: { label: 'Needs Correction', className: 'bg-orange-500 text-white', icon: RotateCcw },
  // Legacy support
  DRAFT: { label: 'Draft', className: 'bg-gray-500 text-white', icon: FileText },
  SUBMITTED: { label: 'Submitted', className: 'bg-yellow-500 text-black', icon: Clock },
  APPROVED: { label: 'Approved', className: 'bg-green-600 text-white', icon: CheckCircle },
  REJECTED: { label: 'Rejected', className: 'bg-red-600 text-white', icon: XCircle },
};

function AttendanceBadge({ status }) {
  const config = ATTENDANCE_BADGE_CONFIG[status] || { label: status || '-', className: 'bg-gray-500 text-white', icon: Clock };
  const Icon = config.icon;
  return (
    <Badge className={`${config.className} text-xs flex items-center gap-1`} data-testid="attendance-badge">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function ApprovalBadge({ status }) {
  const config = APPROVAL_BADGE_CONFIG[status] || { label: status || '-', className: 'bg-gray-500 text-white', icon: Clock };
  const Icon = config.icon;
  return (
    <Badge className={`${config.className} text-xs flex items-center gap-1`} data-testid="approval-badge">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ENHANCED ATTENDANCE SUMMARY COMPONENT (Day 10)
// ═══════════════════════════════════════════════════════════════════════════════

// P3 — feature-flag helper for the Care Plan v2 Structured Matrix. Used to
// (1) mount the Staff Shift Brief card on this page, and (2) flip the
// Acknowledge flow to the multi-stakeholder v2 dialog so the brief is
// embedded inside the pre-check-in acknowledgement modal.
const structuredMatrixFlagOn = () => {
  try {
    return !!(JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}'))
      .care_plan_v2_structured_matrix_enabled;
  } catch { return false; }
};
const carePlanV2FlagOn = () => {
  try {
    return !!(JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}'))
      .care_plan_v2_enabled;
  } catch { return false; }
};

function AttendanceSummaryCard({ shift, timesheet }) {
  const scheduledStart = shift?.start_time || '-';
  const scheduledEnd = shift?.end_time || '-';
  const actualClockIn = shift?.check_in_time ? new Date(shift.check_in_time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '-';
  const actualClockOut = shift?.check_out_time ? new Date(shift.check_out_time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '-';
  
  // Get variance from timesheet if available
  const lateMinutes = timesheet?.late_minutes || 0;
  const earlyLeaveMinutes = timesheet?.early_leave_minutes || 0;
  const overtimeMinutes = timesheet?.overtime_minutes || 0;
  const attendanceStatus = timesheet?.attendance_status || null;

  return (
    <Card className="border-blue-200 bg-blue-50/50" data-testid="attendance-summary-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-600" />
          Attendance Summary
        </CardTitle>
        <CardDescription>Your attendance evidence for this shift</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Time Comparison Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground uppercase font-medium">Scheduled</p>
            <div className="bg-white rounded-lg p-3 border">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="font-mono">{scheduledStart} - {scheduledEnd}</span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground uppercase font-medium">Actual</p>
            <div className="bg-white rounded-lg p-3 border">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="font-mono">{actualClockIn} - {actualClockOut}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Attendance Status */}
        {attendanceStatus && (
          <div className="flex items-center justify-between bg-white rounded-lg p-3 border">
            <span className="text-sm text-muted-foreground">Status</span>
            <AttendanceBadge status={attendanceStatus} />
          </div>
        )}

        {/* Variance Summary */}
        {(lateMinutes > 0 || earlyLeaveMinutes > 0 || overtimeMinutes > 0) && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground uppercase font-medium">Variance</p>
            <div className="flex flex-wrap gap-2">
              {lateMinutes > 0 && (
                <Badge
                  variant="outline"
                  className={`bg-yellow-50 text-yellow-700 border-yellow-200 ${isLargeVariance(lateMinutes) ? 'bg-red-50 text-red-700 border-red-200' : ''}`}
                  title={formatVarianceTitle('Late sign-in by', lateMinutes)}
                  data-testid="late-variance"
                >
                  {formatLateBadge(lateMinutes)}
                </Badge>
              )}
              {earlyLeaveMinutes > 0 && (
                <Badge
                  variant="outline"
                  className={`bg-orange-50 text-orange-700 border-orange-200 ${isLargeVariance(earlyLeaveMinutes) ? 'bg-red-50 text-red-700 border-red-200' : ''}`}
                  title={formatVarianceTitle('Early sign-out by', earlyLeaveMinutes)}
                  data-testid="early-variance"
                >
                  {formatEarlyLeaveBadge(earlyLeaveMinutes)}
                </Badge>
              )}
              {overtimeMinutes > 0 && (
                <Badge
                  variant="outline"
                  className={`bg-blue-50 text-blue-700 border-blue-200 ${isLargeVariance(overtimeMinutes) ? 'bg-red-50 text-red-700 border-red-200' : ''}`}
                  title={formatVarianceTitle('Overtime:', overtimeMinutes)}
                  data-testid="overtime-variance"
                >
                  {formatOvertimeBadge(overtimeMinutes)}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* On Time indicator */}
        {attendanceStatus === 'on_time' && (
          <div className="flex items-center gap-2 text-green-600 bg-green-50 rounded-lg p-3">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm font-medium">You were on time for this shift</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// READ-ONLY TIMESHEET STATUS CARD (Day 10 - Replaces Legacy Submit UI)
// ═══════════════════════════════════════════════════════════════════════════════

function TimesheetStatusCard({ timesheet, shift }) {
  if (!timesheet && shift?.status !== 'COMPLETED') {
    return null;
  }

  const approvalStatus = timesheet?.approval_status || timesheet?.status;
  const scheduledHours = timesheet?.base_duration_hours || timesheet?.hours_worked || 0;
  const approvedHours = timesheet?.approved_duration_hours;

  return (
    <Card data-testid="timesheet-status-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Your Timesheet Status
        </CardTitle>
        <CardDescription>
          Timesheets are automatically created when you check out
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {timesheet ? (
          <>
            {/* Status Row */}
            <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
              <span className="text-sm font-medium">Approval Status</span>
              <ApprovalBadge status={approvalStatus} />
            </div>

            {/* Hours Information */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Scheduled Hours</p>
                <p className="text-lg font-mono font-bold">{scheduledHours.toFixed(2)}h</p>
              </div>
              {approvedHours !== undefined && approvedHours !== null && (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <p className="text-xs text-green-600">Approved Hours</p>
                  <p className="text-lg font-mono font-bold text-green-700">{approvedHours.toFixed(2)}h</p>
                </div>
              )}
            </div>

            {/* Status-specific messages */}
            {(approvalStatus === 'pending_approval' || approvalStatus === 'SUBMITTED' || approvalStatus === 'DRAFT') && (
              <Alert className="border-yellow-200 bg-yellow-50">
                <Clock className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  Your timesheet is being reviewed by an administrator. You will be notified once it's approved.
                </AlertDescription>
              </Alert>
            )}

            {(approvalStatus === 'approved_scheduled' || approvalStatus === 'approved_adjusted' || approvalStatus === 'APPROVED') && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Your timesheet has been approved and is ready for payment processing.
                </AlertDescription>
              </Alert>
            )}

            {(approvalStatus === 'rejected' || approvalStatus === 'REJECTED') && (
              <Alert className="border-red-200 bg-red-50">
                <XCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  Your timesheet was not approved. {timesheet.rejection_reason && `Reason: ${timesheet.rejection_reason}`}
                </AlertDescription>
              </Alert>
            )}

            {approvalStatus === 'returned_for_correction' && (
              <Alert className="border-orange-200 bg-orange-50">
                <RotateCcw className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  Your timesheet needs correction. {timesheet.correction_notes && `Notes: ${timesheet.correction_notes}`}
                  <br />
                  <span className="text-xs">An administrator will update your attendance records.</span>
                </AlertDescription>
              </Alert>
            )}
          </>
        ) : (
          <Alert className="border-blue-200 bg-blue-50">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              A timesheet will be automatically created when you complete this shift.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEGACY TIMESHEET SECTION (Feature-flagged - Hidden when upgrade enabled)
// ═══════════════════════════════════════════════════════════════════════════════

function LegacyTimesheetSection({ 
  existingTimesheet, 
  timesheetNotes, 
  setTimesheetNotes, 
  timesheetHours, 
  setTimesheetHours, 
  handleCreateTimesheet, 
  handleSubmitTimesheet, 
  actionLoading,
  autoSubmitOnClockoutEnabled = false,
}) {
  return (
    <Card data-testid="legacy-timesheet-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" /> Timesheet
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {existingTimesheet ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <StatusBadge status={existingTimesheet.status} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Hours</span>
              <span className="font-mono tabular-nums">{existingTimesheet.hours_worked}h</span>
            </div>
            {existingTimesheet.status === 'DRAFT' && !autoSubmitOnClockoutEnabled && (
              <>
                <div className="space-y-2">
                  <Label>Adjust Hours</Label>
                  <Input type="number" step="0.25" value={timesheetHours || existingTimesheet.hours_worked} onChange={(e) => setTimesheetHours(e.target.value)} data-testid="timesheet-hours-input" />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input value={timesheetNotes} onChange={(e) => setTimesheetNotes(e.target.value)} placeholder="Add notes..." data-testid="timesheet-notes-input" />
                </div>
                <Button className="w-full" onClick={handleSubmitTimesheet} disabled={actionLoading} data-testid="timesheet-submit-button">
                  {actionLoading ? 'Submitting...' : 'Submit for Approval'}
                </Button>
              </>
            )}
            {existingTimesheet.status === 'DRAFT' && autoSubmitOnClockoutEnabled && (
              <p
                className="text-sm text-muted-foreground text-center py-2"
                data-testid="timesheet-auto-submitted-notice"
              >
                Timesheet submitted automatically for approval.
              </p>
            )}
            {existingTimesheet.status === 'SUBMITTED' && (
              <p
                className="text-sm text-muted-foreground text-center py-2"
                data-testid="timesheet-auto-submitted-notice"
              >
                {autoSubmitOnClockoutEnabled
                  ? 'Timesheet submitted automatically for approval.'
                  : 'Awaiting admin approval'}
              </p>
            )}
            {existingTimesheet.status === 'APPROVED' && (
              <p className="text-sm text-emerald-600 text-center py-2">Timesheet approved</p>
            )}
          </div>
        ) : (
          autoSubmitOnClockoutEnabled ? (
            <p
              className="text-sm text-muted-foreground text-center py-2"
              data-testid="timesheet-auto-submit-pending"
            >
              Your timesheet will be submitted automatically when you clock out.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input value={timesheetNotes} onChange={(e) => setTimesheetNotes(e.target.value)} placeholder="Describe the work done..." data-testid="timesheet-notes-input" />
              </div>
              <Button className="w-full" onClick={handleCreateTimesheet} disabled={actionLoading} data-testid="timesheet-create-button">
                {actionLoading ? 'Creating...' : 'Create Timesheet'}
              </Button>
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function ShiftDetail() {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  // Clock-in window: 2 h before start until the shift ends (the server enforces it). Re-checked every 30 s so the button
  // opens on its own when the time comes.
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowMs(Date.now()), 30000); return () => clearInterval(t); }, []);
  const [gpsStatus, setGpsStatus] = useState('idle'); // idle, locating, done, denied
  const [coords, setCoords] = useState(null);
  const [manualDialog, setManualDialog] = useState({ open: false, action: null, reason: '' });
  const [clockComment, setClockComment] = useState('');
  const [timesheetNotes, setTimesheetNotes] = useState('');
  const [timesheetHours, setTimesheetHours] = useState('');
  const [existingTimesheet, setExistingTimesheet] = useState(null);
  const [progressNotes, setProgressNotes] = useState([]);
  const [noteForm, setNoteForm] = useState(emptyNoteForm());
  const [noteRequired, setNoteRequired] = useState(false);
  // Day 9: shared worker context — same GET /mobile/shifts/{id} payload the Mobile App reads
  const [workerContext, setWorkerContext] = useState(null);
  const [noteParticipantId, setNoteParticipantId] = useState(null);
  const fetchWorkerContext = useCallback(async () => {
    try {
      const r = await api.get(`/mobile/shifts/${shiftId}`);
      setWorkerContext(r.data?.data?.workerContext || null);
    } catch (_) { setWorkerContext(null); }
  }, [shiftId]);
  useEffect(() => { fetchWorkerContext(); }, [fetchWorkerContext]);
  
  // Day 10: Feature flag state
  const [staffUiUpgradeEnabled, setStaffUiUpgradeEnabled] = useState(false);
  // Feb 2026: Auto-submit-on-clock-out flag
  const [autoSubmitOnClockoutEnabled, setAutoSubmitOnClockoutEnabled] = useState(false);

  const fetchShift = useCallback(async () => {
    try {
      // Phase 3D fix — single-shift endpoint returns `medications_due` (the list endpoint
      // omits it). Falls back to my-shifts list for legacy behaviour if detail call fails.
      try {
        const detail = await api.get(`/roster/shifts/${shiftId}`);
        const shiftDoc = detail.data?.data || detail.data;
        if (shiftDoc && (shiftDoc.id === shiftId || shiftDoc._id === shiftId)) {
          setShift(shiftDoc);
          return;
        }
      } catch (_eDetail) {
        // fall through to list-based lookup
      }
      const myShifts = await api.get('/roster/my-shifts');
      // API may return either `{items: [...]}` (paginated) or `{data: [...]}`
      // (legacy). Tolerate both to avoid the page bouncing back to /dashboard.
      const list = myShifts.data.items || myShifts.data.data || [];
      const found = list.find(s => s.id === shiftId);
      if (found) {
        setShift(found);
      } else {
        notify.error(null, 'SHIFT_NOT_FOUND');
        navigate('/staff/roster');
      }
    } catch (e) {
      notify.error(null, 'LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, [shiftId, navigate]);

  // client#10: the clock-out checklist links to `/staff/shift/:id#progress-notes` — open at the Progress Notes card
  const shiftLoaded = !!shift;
  useEffect(() => {
    if (!shiftLoaded || window.location.hash !== '#progress-notes') return;
    const t = setTimeout(() => document.getElementById('progress-notes-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => clearTimeout(t);
  }, [shiftLoaded]);

  const fetchTimesheet = useCallback(async () => {
    try {
      // Try V2 endpoint first for enhanced data
      const res = await api.get('/timesheets/v2/my');
      const items = res.data.items || res.data.data || [];
      const ts = items.find(t => t.shift_id === shiftId);
      if (ts) {
        setExistingTimesheet(ts);
        return;
      }
    } catch (e) {
      // Fallback to legacy endpoint
    }
    
    try {
      const res = await api.get('/timesheets/my');
      const ts = (res.data.data || []).find(t => t.shift_id === shiftId);
      if (ts) setExistingTimesheet(ts);
    } catch (e) {
      // Ignore
    }
  }, [shiftId]);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await api.get(`/notes/by-shift/${shiftId}`);
      setProgressNotes(res.data.data || []);
    } catch (e) {}
  }, [shiftId]);

  const checkNoteRequired = useCallback(async () => {
    try {
      const res = await api.get('/org/settings');
      setNoteRequired(res.data.data?.require_progress_note_before_timesheet_submit || false);
    } catch (e) {}
  }, []);

  // Day 10: Fetch feature flag
  const fetchFeatureFlags = useCallback(async () => {
    try {
      const res = await api.get('/timesheets/v2/config');
      setStaffUiUpgradeEnabled(res.data.data?.staff_attendance_ui_upgrade_enabled || false);
      setAutoSubmitOnClockoutEnabled(
        res.data.data?.auto_submit_timesheet_on_clockout_enabled !== false
      );
    } catch (e) {
      // Default to disabled if config fetch fails
      setStaffUiUpgradeEnabled(false);
      setAutoSubmitOnClockoutEnabled(false);
    }
  }, []);

  useEffect(() => {
    fetchShift();
    fetchTimesheet();
    fetchNotes();
    checkNoteRequired();
    fetchFeatureFlags();
  }, [fetchShift, fetchTimesheet, fetchNotes, checkNoteRequired, fetchFeatureFlags]);

  const getGPS = () => {
    return new Promise((resolve, reject) => {
      setGpsStatus('locating');
      if (!navigator.geolocation) {
        setGpsStatus('denied');
        reject(new Error('GEO_UNSUPPORTED'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCoords(c);
          setGpsStatus('done');
          resolve(c);
        },
        (err) => {
          // INTEGRITY: never fabricate/default coordinates and submit them as
          // genuine worker location. On denial/unavailable/timeout we reject so
          // the caller can require a real fix (or an authorised manual exception).
          setGpsStatus('denied');
          reject(new Error('GEO_DENIED'));
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const doCheckIn = async (payload) => {
    try {
      await api.post(`/roster/shifts/${shiftId}/check-in`, payload);
      toast.success('Checked in successfully!');
      fetchShift();
      return true;
    } catch (e) {
      const code = e?.response?.data?.error?.code || e?.response?.data?.detail?.code;
      if (code === 'CARE_PLAN_ACK_REQUIRED') {
        notify.error(e, 'CARE_PLAN_ACK_REQUIRED');
        setTimeout(() => document.getElementById('care-plan-ack-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
      } else {
        notify.error(e, 'CHECKIN_FAILED');
      }
      return false;
    }
  };

  const doCheckOut = async (payload) => {
    try {
      await api.post(`/roster/shifts/${shiftId}/check-out`, payload);
      toast.success('Checked out successfully!');
      fetchShift();
      setTimeout(() => fetchTimesheet(), 1000);
      return true;
    } catch (e) {
      notify.error(e, 'CHECKOUT_FAILED');
      return false;
    }
  };

  const handleCheckIn = async () => {
    setActionLoading(true);
    let gps;
    try {
      gps = await getGPS();
    } catch (geoErr) {
      // Location unavailable — offer the authorised MANUAL exception (no fake coords).
      setActionLoading(false);
      setManualDialog({ open: true, action: 'check-in', reason: '' });
      return;
    }
    const notes = clockComment.trim() || undefined;
    const ok = await doCheckIn({ ...gps, notes });
    if (ok) setClockComment('');
    setActionLoading(false);
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    let gps;
    try {
      gps = await getGPS();
    } catch (geoErr) {
      setActionLoading(false);
      setManualDialog({ open: true, action: 'check-out', reason: '' });
      return;
    }
    const notes = clockComment.trim() || undefined;
    const ok = await doCheckOut({ ...gps, notes });
    if (ok) setClockComment('');
    setActionLoading(false);
  };

  const submitManual = async () => {
    const reason = (manualDialog.reason || '').trim();
    if (!reason) return; // mandatory reason; also enforced by backend
    setActionLoading(true);
    const payload = { method: 'MANUAL', manualReason: reason, notes: clockComment.trim() || undefined };
    const ok = manualDialog.action === 'check-in' ? await doCheckIn(payload) : await doCheckOut(payload);
    setActionLoading(false);
    if (ok) { setManualDialog({ open: false, action: null, reason: '' }); setClockComment(''); }
  };

  // Legacy handlers (only used when feature flag is disabled)
  const handleCreateTimesheet = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/timesheets/${shiftId}/submit`, {
        notes: timesheetNotes || '',
        hours_worked: timesheetHours ? parseFloat(timesheetHours) : undefined,
      });
      setExistingTimesheet(res.data.data);
      toast.success('Timesheet submitted for approval');
    } catch (e) {
      notify.error(e, 'TIMESHEET_SUBMIT_FAILED');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitTimesheet = async () => {
    if (!existingTimesheet) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/timesheets/${existingTimesheet.id}/submit`, {
        hours_worked: timesheetHours ? parseFloat(timesheetHours) : undefined,
        notes: timesheetNotes || undefined,
      });
      setExistingTimesheet(res.data.data);
      toast.success('Timesheet submitted for approval');
    } catch (e) {
      notify.error(e, 'TIMESHEET_SUBMIT_FAILED');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!shift) return null;

  // Per-staff attendance derivations — critical for 2:1 multi-staff shifts so
  // that each worker drives their own check-in/out buttons off their OWN
  // attendance record, not the shift-level (legacy first-staff) stamps.
  const assignedIds = (shift.assigned_staff_ids && shift.assigned_staff_ids.length)
    ? shift.assigned_staff_ids
    : (shift.assigned_staff_id ? [shift.assigned_staff_id] : []);
  const isMultiStaffShift = assignedIds.length > 1;
  const myAttendance = (shift.staff_attendance || {})[user?.id] || {};
  // For single-staff shifts fall back to legacy shift-level stamps so existing
  // 1:1 flows continue to work unchanged.
  const myCheckInTime = isMultiStaffShift
    ? (myAttendance.check_in_time || null)
    : (myAttendance.check_in_time || shift.check_in_time || null);
  const myCheckOutTime = isMultiStaffShift
    ? (myAttendance.check_out_time || null)
    : (myAttendance.check_out_time || shift.check_out_time || null);
  const teammatesPendingCheckout = isMultiStaffShift
    ? assignedIds.filter(sid => sid !== user?.id && !((shift.staff_attendance || {})[sid] || {}).check_out_time).length
    : 0;

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="shift-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/staff/roster')} className="-ml-2" data-testid="back-to-roster">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to roster
      </Button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Shift Detail</h1>
          <p className="text-muted-foreground mt-1">{shift.date} | {shift.start_time} - {shift.end_time}</p>
        </div>
      </div>

      {/* Phase 2D PR-3 — Safety Alerts Widget (redacted shift_context view) */}
      {shift.participant_id && (
        <RiskProfilePanel participantId={shift.participant_id} mode="shift_context" />
      )}

      {/* Shift Info Card */}
      <Card data-testid="shift-info-card">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-2" data-testid="shift-participants">
              <User className="h-4 w-4 text-muted-foreground mt-0.5" />
              {(shift.linked_participant_details && shift.linked_participant_details.length > 1) ? (
                <div>
                  <span className="text-muted-foreground">Participants ({shift.linked_participant_details.length}):</span>
                  <ul className="mt-1 space-y-0.5">
                    {shift.linked_participant_details.map((p) => (
                      <li key={p.id} className="font-medium" data-testid={`shift-participant-${p.id}`}>{p.name || 'N/A'}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div>
                  <span className="text-muted-foreground">Participant:</span>{' '}
                  <span className="font-medium">{shift.participant_name || 'N/A'}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Date:</span> 
              <span className="font-mono tabular-nums">{shift.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Time:</span> 
              <span className="font-mono tabular-nums">{shift.start_time} - {shift.end_time}</span>
            </div>
          </div>

          {/* client#7: where to go + what the office noted for this shift */}
          <div className="space-y-2 border-t pt-3" data-testid="shift-where-notes">
            <ShiftAddress shift={shift} />
            <ShiftNotes notes={shift.notes} />
          </div>

          {(shift.linked_participant_details && shift.linked_participant_details.length > 1) ? (
            shift.linked_participant_details.map((p) => (
              <Button
                key={p.id}
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => navigate(`/staff/participants/${p.id}`)}
                data-testid={`shift-view-participant-profile-${p.id}`}
              >
                <User className="h-4 w-4 mr-2" /> View Profile — {p.name || 'Participant'}
              </Button>
            ))
          ) : (shift.participant_id && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => navigate(`/staff/participants/${shift.participant_id}`)}
              data-testid="shift-view-participant-profile"
            >
              <User className="h-4 w-4 mr-2" /> View Participant Profile
            </Button>
          ))}

          {shift.participant_id && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => navigate(`/staff/participants/${shift.participant_id}?tab=tasks`)}
              data-testid="shift-view-tasks"
            >
              <ListChecks className="h-4 w-4 mr-2" /> View Tasks
            </Button>
          )}

          {shift.check_in_time && (
            <>
              <Separator />
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <span>Checked in: {new Date(myCheckInTime || shift.check_in_time).toLocaleTimeString()}</span>
                </div>
                {(myCheckOutTime || shift.check_out_time) && (
                  <div className="flex items-center gap-2">
                    <LogOut className="h-4 w-4 text-blue-600" />
                    <span>Checked out: {new Date(myCheckOutTime || shift.check_out_time).toLocaleTimeString()}</span>
                  </div>
                )}
                {shift.check_in_lat && (
                  <div className="flex items-center gap-2 col-span-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">GPS: ({shift.check_in_lat?.toFixed(4)}, {shift.check_in_lng?.toFixed(4)})</span>
                  </div>
                )}
                {(myAttendance.check_in_notes || shift.check_in_notes) && (
                  <div className="flex items-start gap-2 col-span-2" data-testid="shift-detail-checkin-note">
                    <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span className="text-sm"><span className="text-muted-foreground">Check-in comment:</span> {myAttendance.check_in_notes || shift.check_in_notes}</span>
                  </div>
                )}
                {(myAttendance.check_out_notes || shift.check_out_notes) && (
                  <div className="flex items-start gap-2 col-span-2" data-testid="shift-detail-checkout-note">
                    <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span className="text-sm"><span className="text-muted-foreground">Check-out comment:</span> {myAttendance.check_out_notes || shift.check_out_notes}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Day 9: participants on this shift — multi-participant, participant-specific times/tasks/goals/notes/attendance, evidence prompts */}
      <WorkerShiftParticipantsPanel
        shiftId={shiftId}
        shiftDate={shift.date}
        context={workerContext}
        onRefresh={() => { fetchWorkerContext(); fetchNotes(); }}
        onAddNote={(participantId) => { setNoteParticipantId(participantId); document.getElementById('progress-notes-section')?.scrollIntoView({ behavior: 'smooth' }); }}
        onOpenProfile={(participantId) => navigate(`/staff/participants/${participantId}`)}
        onPromptAction={(p) => {
          if (p.action === 'NOTE') { setNoteParticipantId(p.participantId); document.getElementById('progress-notes-section')?.scrollIntoView({ behavior: 'smooth' }); }
          else if (p.action === 'TRANSPORT') navigate(`/my/trip-logs?shift_id=${shiftId}`);
          else if (p.action === 'ATTACHMENT') navigate(`/staff/attachments?entityType=SHIFT&entityId=${shiftId}`);   // BS3-025: staff portal page, not the mobile one
          else if (p.action === 'CHECK_OUT') document.getElementById('checkout-card')?.scrollIntoView({ behavior: 'smooth' });
        }}
      />

      {/* Check-In Action Button — per-staff aware for 2:1 multi-staff shifts.
          Show whenever the shift is active (ASSIGNED or IN_PROGRESS) and the
          CURRENT user hasn't yet stamped their own check-in. Outside the clock-in window (past / future shifts) there is
          no Check In at all, only when it opens or that it has closed. */}
      {(shift.status === 'ASSIGNED' || shift.status === 'IN_PROGRESS') && !myCheckInTime && clockInClosedText(clockInWindow(shift, nowMs)) && (
        <p className="text-sm text-amber-700 flex items-center gap-1.5" data-testid="shift-detail-checkin-closed">
          <Clock className="h-4 w-4" />{clockInClosedText(clockInWindow(shift, nowMs))}
        </p>
      )}
      {(shift.status === 'ASSIGNED' || shift.status === 'IN_PROGRESS') && !myCheckInTime && !clockInClosedText(clockInWindow(shift, nowMs)) && (
        <Card className="border-primary/20" data-testid="checkin-card">
          <CardContent className="p-4">
            {gpsStatus === 'denied' && (
              <Alert className="mb-4" variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>Location unavailable. Enable location permission to record attendance — no approximate location is submitted.</AlertDescription>
              </Alert>
            )}
            <div className="mb-3 space-y-1.5">
              <Label htmlFor="checkin-comment" className="text-xs text-muted-foreground flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5" /> Comment (optional)
              </Label>
              <Textarea
                id="checkin-comment"
                value={clockComment}
                onChange={(e) => setClockComment(e.target.value)}
                placeholder="Add a comment about your check-in"
                rows={2}
                data-testid="shift-detail-checkin-comment"
              />
            </div>
            <Button
              size="lg"
              className="w-full rounded-xl"
              onClick={handleCheckIn}
              disabled={actionLoading}
              data-testid="shift-detail-checkin-button"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MapPin className="h-4 w-4 mr-2" />}
              {actionLoading ? 'Locating...' : 'Check In'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Check-Out Action Button — only when THIS staff has checked in but not
          yet checked out. (Was previously shift.status==='IN_PROGRESS' which
          blocked the 2nd staff on a 2:1 from ever checking in.) */}
      {myCheckInTime && !myCheckOutTime && (
        <Card className="border-amber-200" data-testid="checkout-card" id="checkout-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Your shift in progress since {new Date(myCheckInTime).toLocaleTimeString()}</span>
            </div>
            <div className="mb-3 space-y-1.5">
              <Label htmlFor="checkout-comment" className="text-xs text-muted-foreground flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5" /> Comment (optional)
              </Label>
              <Textarea
                id="checkout-comment"
                value={clockComment}
                onChange={(e) => setClockComment(e.target.value)}
                placeholder="Add a comment about your check-out"
                rows={2}
                data-testid="shift-detail-checkout-comment"
              />
            </div>
            <Button
              size="lg"
              className="w-full rounded-xl"
              variant="secondary"
              onClick={handleCheckOut}
              disabled={actionLoading}
              data-testid="shift-detail-checkout-button"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />}
              {actionLoading ? 'Locating...' : 'Check Out'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 2:1 multi-staff progress indicator — surfaces only when this is a
          multi-staff shift so each worker knows whether they're waiting on
          their teammate before status flips to COMPLETED. */}
      {isMultiStaffShift && myCheckOutTime && shift.status === 'IN_PROGRESS' && (
        <Card className="border-emerald-200 bg-emerald-50/60" data-testid="multi-staff-waiting-card">
          <CardContent className="p-3 text-xs text-emerald-900">
            You've checked out. Waiting on {teammatesPendingCheckout} teammate{teammatesPendingCheckout === 1 ? '' : 's'} to complete the shift.
          </CardContent>
        </Card>
      )}

      {/* Day 10: Enhanced Attendance Summary (only when shift completed and upgrade enabled) */}
      {staffUiUpgradeEnabled && shift.status === 'COMPLETED' && (
        <AttendanceSummaryCard shift={shift} timesheet={existingTimesheet} />
      )}

      {/* Timesheet Section - Feature-flagged */}
      {shift.status === 'COMPLETED' && (
        staffUiUpgradeEnabled ? (
          // Day 10: Read-only timesheet status card
          <TimesheetStatusCard timesheet={existingTimesheet} shift={shift} />
        ) : (
          // Legacy: Staff can create/submit timesheets
          <LegacyTimesheetSection
            existingTimesheet={existingTimesheet}
            timesheetNotes={timesheetNotes}
            setTimesheetNotes={setTimesheetNotes}
            timesheetHours={timesheetHours}
            setTimesheetHours={setTimesheetHours}
            handleCreateTimesheet={handleCreateTimesheet}
            handleSubmitTimesheet={handleSubmitTimesheet}
            actionLoading={actionLoading}
            autoSubmitOnClockoutEnabled={autoSubmitOnClockoutEnabled}
          />
        )
      )}

      {/* Progress Notes Section (client#10: the clock checklist links here with #progress-notes) */}
      {shift && ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'APPROVED'].includes(shift.status) && (
        <Card data-testid="progress-notes-section" id="progress-notes-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> Progress Notes
              {noteRequired && !progressNotes.some(n => n.status === 'SUBMITTED') && (
                <span className="text-xs text-destructive font-normal">(Required before timesheet submit)</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Existing notes */}
            {progressNotes.length > 0 && (
              <div className="space-y-2 mb-4">
                {progressNotes.map(n => (
                  <div key={n.id} className="border rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{n.title || n.template_key}</span>
                      <StatusBadge status={n.status} />
                    </div>
                    <p className="text-muted-foreground text-xs">{n.note?.slice(0, 100)}{n.note?.length > 100 ? '...' : ''}</p>
                  </div>
                ))}
              </div>
            )}

            {/* New note form */}
            {!progressNotes.some(n => n.status === 'SUBMITTED') && (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                <p className="text-sm font-medium">Write Progress Note</p>
                {workerContext?.isMultiParticipant && (
                  <div>
                    <Label className="text-xs">Participant</Label>
                    <select className="w-full rounded-md border bg-background px-2 py-2 text-sm" value={noteParticipantId || shift.participant_id || ''} onChange={(e) => setNoteParticipantId(e.target.value)} data-testid="note-participant-select">
                      {workerContext.participants.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                )}
                <ProgressNoteForm form={noteForm} onChange={setNoteForm} />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={!noteForm.note || actionLoading} onClick={async () => {
                    try {
                      await api.post('/notes', { shift_id: shiftId, participant_id: noteParticipantId || shift.participant_id, ...noteForm });
                      toast.success('Note saved as draft'); setNoteForm(emptyNoteForm()); fetchNotes(); fetchWorkerContext();
                    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
                  }} data-testid="note-save-draft">Save Draft</Button>
                  <Button size="sm" disabled={!noteForm.note || actionLoading} onClick={async () => {
                    try {
                      const res = await api.post('/notes', { shift_id: shiftId, participant_id: noteParticipantId || shift.participant_id, ...noteForm });
                      await api.post(`/notes/${res.data.data.id}/submit`);
                      toast.success('Note submitted'); setNoteForm(emptyNoteForm()); fetchNotes(); fetchWorkerContext();
                    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
                  }} data-testid="note-save-submit"><Send className="h-3 w-3 mr-1" /> Save & Submit</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Care Plan Acknowledgement */}
      {shift && shift.status === 'ASSIGNED' && (
        <AckAndHandoverSection shiftId={shiftId} shift={shift} onAcknowledged={fetchShift} />
      )}

      {/* P3 Structured Matrix — Staff Shift Brief (read-only) shown whenever
          the structured flag is on and the shift has a participant. Mounted
          here so it appears before/alongside the ack flow when the shift is
          ASSIGNED, and remains visible IN_PROGRESS / COMPLETED for reference. */}
      {shift && shift.participant_id && structuredMatrixFlagOn() && (
        <Card data-testid="shift-detail-staff-brief-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> Pre-Shift Care Brief
            </CardTitle>
            <CardDescription>Read this before checking in. Internal-only notes are hidden from support workers.</CardDescription>
          </CardHeader>
          <CardContent>
            <StaffShiftBriefView shiftId={shiftId} />
          </CardContent>
        </Card>
      )}

      {/* eMAR - Medication Administration */}
      {shift && shift.medications_due && shift.medications_due.length > 0 && (
        <EmarSection shiftId={shiftId} shift={shift} medicationsDue={shift.medications_due} />
      )}

      {/* Handover Notes */}
      {shift && ['COMPLETED', 'APPROVED', 'IN_PROGRESS'].includes(shift.status) && (
        <HandoverSection shiftId={shiftId} shift={shift} />
      )}

      {/* Manual Location Exception — offered only when genuine GPS is unavailable.
          Never fabricates coordinates; requires a mandatory reason (backend-authorised). */}
      <Dialog open={manualDialog.open} onOpenChange={(o) => !o && setManualDialog({ open: false, action: null, reason: '' })}>
        <DialogContent data-testid="manual-location-dialog">
          <DialogHeader>
            <DialogTitle>Location could not be obtained</DialogTitle>
            <DialogDescription>
              Your device location is unavailable, so this {manualDialog.action === 'check-out' ? 'check-out' : 'check-in'} will be recorded as a manual exception. Explain why location could not be captured.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="manual-reason" className="text-xs">Reason (required)</Label>
            <textarea
              id="manual-reason"
              data-testid="manual-location-reason"
              className="w-full min-h-[90px] rounded-lg border bg-background px-3 py-2 text-sm"
              value={manualDialog.reason}
              onChange={(e) => setManualDialog({ ...manualDialog, reason: e.target.value })}
              placeholder="e.g. GPS signal unavailable inside the building"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setManualDialog({ open: false, action: null, reason: '' })} data-testid="manual-location-cancel">Cancel</Button>
            <Button onClick={submitManual} disabled={!manualDialog.reason.trim() || actionLoading} data-testid="manual-location-submit">
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Submit manual {manualDialog.action === 'check-out' ? 'check-out' : 'check-in'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AckAndHandoverSection({ shiftId, shift, onAcknowledged }) {
  const [carePlan, setCarePlan] = useState(null);
  const [acked, setAcked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [v2DialogOpen, setV2DialogOpen] = useState(false);
  const useV2Dialog = carePlanV2FlagOn();

  useEffect(() => {
    const check = async () => {
      try {
        const pid = shift.participant_id;
        if (!pid) { setLoading(false); return; }
        const r = await api.get(`/careplans/by-participant/${pid}?pageSize=10`);
        const plans = r.data.items || r.data.data || [];
        const active = plans.find(p => p.status === 'APPROVED');
        if (!active) { setLoading(false); return; }
        setCarePlan(active);
        // Idempotency: if this shift has already been acknowledged on this plan
        // (either via legacy POST /acknowledge or v2 /acknowledge-signoff) we
        // suppress the yellow nag card so it does not reappear after refresh.
        const acks = active.acknowledgements || [];
        if (acks.some(a => a && a.shift_id === shiftId)) {
          setAcked(true);
        }
        // Check if already acknowledged
        // Try check-in dry-run concept: just check ack collection
        const ackCheck = await api.get(`/handover/by-shift/${shiftId}`);
        // Actually we need to check ack endpoint - for now show the form
      } catch (e) {}
      setLoading(false);
    };
    check();
  }, [shiftId, shift.participant_id]);

  // Legacy single-signer ack (kept for flag-OFF rollback parity)
  const acknowledge = async () => {
    if (!carePlan) return;
    try {
      await api.post(`/careplans/${carePlan.id}/acknowledge`, { shift_id: shiftId, notes: 'Reviewed and understood' });
      toast.success('Care plan acknowledged');
      setAcked(true);
      if (typeof onAcknowledged === 'function') onAcknowledged();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  // v2 multi-stakeholder ack — POSTs the signed-off payload and is opened
  // from the same yellow card. The dialog embeds <StaffShiftBriefView>
  // above the form whenever a `shiftId` prop is supplied AND the
  // structured matrix flag is ON.
  const submitV2Ack = async (payload) => {
    if (!carePlan) return;
    try {
      await api.post(`/careplans/${carePlan.id}/acknowledge-signoff`, {
        shift_id: shiftId,
        ...payload,
      });
      toast.success('Care plan acknowledged');
      setAcked(true);
      setV2DialogOpen(false);
      if (typeof onAcknowledged === 'function') onAcknowledged();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  if (loading || !carePlan || acked) return null;

  return (
    <>
      <Card className="border-amber-200 bg-amber-50/50" id="care-plan-ack-card" data-testid="care-plan-ack-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-sm">Care Plan Acknowledgement Required</p>
              <p className="text-xs text-muted-foreground mt-1">Please review the care plan for this participant before checking in.</p>
              <p className="text-xs mt-2"><strong>{carePlan.title}</strong> (v{carePlan.version})</p>
              {carePlan.medical_alerts && <p className="text-xs text-red-600 mt-1">Medical Alerts: {carePlan.medical_alerts}</p>}
              {useV2Dialog ? (
                <Button size="sm" className="mt-3" onClick={() => setV2DialogOpen(true)} data-testid="ack-careplan-button">
                  <CheckCircle className="h-3 w-3 mr-1" /> Acknowledge Care Plan
                </Button>
              ) : (
                <Button size="sm" className="mt-3" onClick={acknowledge} data-testid="ack-careplan-button">
                  <CheckCircle className="h-3 w-3 mr-1" /> Acknowledge Care Plan
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {useV2Dialog && (
        <CarePlanAcknowledgementDialog
          open={v2DialogOpen}
          onOpenChange={setV2DialogOpen}
          plan={carePlan}
          shiftId={shiftId}
          onSubmit={submitV2Ack}
        />
      )}
    </>
  );
}

function HandoverSection({ shiftId, shift }) {
  const [handovers, setHandovers] = useState([]);
  const [form, setForm] = useState({ handover_summary: '', participant_wellbeing: '', medication_notes: '', incidents_or_concerns: '', tasks_for_next_shift: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    api.get(`/handover/by-shift/${shiftId}`).then(r => setHandovers(r.data.data || [])).catch(() => {});
  }, [shiftId]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post('/handover', { shift_id: shiftId, participant_id: shift.participant_id, ...form });
      toast.success('Handover submitted');
      const r = await api.get(`/handover/by-shift/${shiftId}`);
      setHandovers(r.data.data || []); setShowForm(false);
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
    finally { setSubmitting(false); }
  };

  return (
    <Card data-testid="handover-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Handover Notes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {handovers.map(h => (
          <div key={h.id} className="border rounded-lg p-3 text-sm space-y-1">
            <p className="font-medium">{h.staff_from_name || 'Staff'}</p>
            <p>{h.handover_summary}</p>
            {h.participant_wellbeing && <p className="text-xs text-muted-foreground">Wellbeing: {h.participant_wellbeing}</p>}
            {h.medication_notes && <p className="text-xs text-muted-foreground">Meds: {h.medication_notes}</p>}
            {h.tasks_for_next_shift && <p className="text-xs text-muted-foreground">Tasks: {h.tasks_for_next_shift}</p>}
          </div>
        ))}
        {!showForm ? (
          <Button size="sm" variant="outline" onClick={() => setShowForm(true)} data-testid="handover-open-form">Write Handover</Button>
        ) : (
          <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
            <div className="space-y-1"><Label className="text-xs">Summary</Label><textarea className="w-full min-h-[60px] rounded-lg border bg-background px-3 py-2 text-sm" value={form.handover_summary} onChange={(e) => setForm({...form, handover_summary: e.target.value})} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Wellbeing</Label><Input className="h-8 text-sm" value={form.participant_wellbeing} onChange={(e) => setForm({...form, participant_wellbeing: e.target.value})} /></div>
              <div className="space-y-1"><Label className="text-xs">Medication Notes</Label><Input className="h-8 text-sm" value={form.medication_notes} onChange={(e) => setForm({...form, medication_notes: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Incidents/Concerns</Label><Input className="h-8 text-sm" value={form.incidents_or_concerns} onChange={(e) => setForm({...form, incidents_or_concerns: e.target.value})} /></div>
              <div className="space-y-1"><Label className="text-xs">Tasks for Next Shift</Label><Input className="h-8 text-sm" value={form.tasks_for_next_shift} onChange={(e) => setForm({...form, tasks_for_next_shift: e.target.value})} /></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={submit} disabled={submitting || !form.handover_summary} data-testid="handover-submit">Submit Handover</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function EmarSection({ shiftId, shift, medicationsDue }) {
  const [adminRecords, setAdminRecords] = useState([]);
  const [recording, setRecording] = useState(null);
  // Phase 3D — Allergy cross-check modal state
  const [allergyModal, setAllergyModal] = useState({ open: false, med: null, matches: [], highestSeverity: null });

  useEffect(() => {
    api.get(`/emar/by-shift/${shiftId}`).then(r => setAdminRecords(r.data.data || [])).catch(() => {});
  }, [shiftId]);

  // Phase 3D — actually call /emar/<status> (extracted so we can call it both directly and after ack)
  const callEmar = async (med, status, ackId = null) => {
    setRecording(med.medication_id + status);
    try {
      const r = await api.post(`/emar/${status.toLowerCase()}`, {
        participant_id: shift.participant_id, medication_id: med.medication_id,
        schedule_id: med.schedule_id, shift_id: shiftId,
        scheduled_time: med.scheduled_time, dose_given: med.dosage,
        ...(ackId ? { allergy_acknowledgement_id: ackId } : {}),
      });
      toast.success(`Medication ${status.toLowerCase()}`);
      if (r.data.data?.incident_suggestion?.suggested) {
        toast.warning(`Incident suggested: ${r.data.data.incident_suggestion.reason}`);
      }
      const refresh = await api.get(`/emar/by-shift/${shiftId}`);
      setAdminRecords(refresh.data.data || []);
    } catch (e) {
      // Phase 3D — defensive net: if the backend returned 409 ALLERGY_ACKNOWLEDGEMENT_REQUIRED
      // (probe was bypassed / network flaked), surface the modal instead of a generic error.
      // The global error envelope wraps payload as { error: { code, message, details: {...} } }.
      const env = e?.response?.data?.error;
      if (e?.response?.status === 409 && env?.code === 'ALLERGY_ACKNOWLEDGEMENT_REQUIRED' && status === 'ADMINISTER') {
        const details = env.details || {};
        setAllergyModal({ open: true, med, matches: details.matches || [], highestSeverity: details.highest_severity });
      } else {
        notify.error(e, 'SAVE_FAILED');
      }
    }
    finally { setRecording(null); }
  };

  const recordMed = async (med, status) => {
    // Phase 3D — Only ADMINISTER triggers the allergy probe. Refuse/Withhold/Miss bypass.
    const isAdminister = status === 'ADMINISTER' || status === 'ADMINISTERED';
    if (!isAdminister) {
      return callEmar(med, status);
    }
    try {
      const probe = await api.get(`/participants/${shift.participant_id}/medications/${med.medication_id}/allergy-check`);
      const data = probe.data || {};
      if (data.feature_enabled && data.requires_acknowledgement && (data.matches || []).length > 0) {
        setAllergyModal({ open: true, med, matches: data.matches, highestSeverity: data.highest_severity });
        return;
      }
    } catch (_e) {
      // Probe failure → fail-soft: proceed without ack so safety check doesn't block administration on a flaky network.
    }
    return callEmar(med, status);
  };

  const acknowledgeAndAdminister = async (reason) => {
    const med = allergyModal.med;
    if (!med) return;
    try {
      const ackRes = await api.post(`/participants/${shift.participant_id}/medications/${med.medication_id}/allergy-check/acknowledge`, {
        shift_id: shiftId, scheduled_time: med.scheduled_time, reason,
      });
      const ackId = ackRes.data?.data?.acknowledgement_id;
      setAllergyModal({ open: false, med: null, matches: [], highestSeverity: null });
      await callEmar(med, 'ADMINISTER', ackId);
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const isRecorded = (medId, time) => adminRecords.some(r => r.medication_id === medId && r.scheduled_time === time);

  return (
    <Card data-testid="emar-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">Medication Administration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {medicationsDue.map((med, i) => {
          const recorded = isRecorded(med.medication_id, med.scheduled_time);
          const record = adminRecords.find(r => r.medication_id === med.medication_id && r.scheduled_time === med.scheduled_time);
          return (
            <div key={i} className={`border rounded-lg p-3 ${recorded ? 'bg-muted/30' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono text-sm font-bold">{med.scheduled_time}</span>
                  <span className="ml-3 font-medium">{med.name}</span>
                  <span className="ml-2 text-sm text-muted-foreground">{med.dosage}</span>
                  {med.requires_double_sign && <span className="ml-2 text-xs text-amber-600 font-medium">(Double-sign)</span>}
                  {med.prn && <span className="ml-2 text-xs text-blue-600">(PRN)</span>}
                </div>
                {recorded ? (
                  <StatusBadge status={record?.status || 'ADMINISTERED'} />
                ) : (
                  <div className="flex gap-1">
                    <Button size="sm" disabled={!!recording} onClick={() => recordMed(med, 'ADMINISTER')} data-testid={`emar-administer-${i}`}>Administer</Button>
                    <Button size="sm" variant="ghost" className="text-amber-600" disabled={!!recording} onClick={() => recordMed(med, 'REFUSE')}>Refuse</Button>
                    <Button size="sm" variant="ghost" className="text-muted-foreground" disabled={!!recording} onClick={() => recordMed(med, 'WITHHOLD')}>Withhold</Button>
                    <Button size="sm" variant="ghost" className="text-destructive" disabled={!!recording} onClick={() => recordMed(med, 'MISS')}>Miss</Button>
                  </div>
                )}
              </div>
              {med.instructions && <p className="text-xs text-muted-foreground mt-1">{med.instructions}</p>}
            </div>
          );
        })}
      </CardContent>
      <MedicationAllergyCheckModal
        open={allergyModal.open}
        matches={allergyModal.matches}
        highestSeverity={allergyModal.highestSeverity}
        onCancel={() => setAllergyModal({ open: false, med: null, matches: [], highestSeverity: null })}
        onAcknowledge={acknowledgeAndAdminister}
      />
    </Card>
  );
}
